import { T_form as T_clientForm } from "./slices/Step1";
import { T_form as T_propertyForm } from "./slices/Step2";

export const ZOHO_MIN_LEASING_VALUE = 1200000;
const BASE_URL = "https://admindrupal.coninsa.co";

type T_option = {
  value: string;
  label: string;
};

type T_options = Array<T_option>;

export type T_fieldSettings = {
  property: {
    types: T_options;
    stratums: T_options;
    branchOffices: T_options;
  };
  client: {
    types: T_options;
    documentTypes: T_options;
    regimeTypes: T_options;
  };
};

export const defaultFieldSettings: T_fieldSettings = {
  property: {
    types: [],
    stratums: [],
    branchOffices: [],
  },
  client: {
    types: [],
    documentTypes: [],
    regimeTypes: [],
  },
};

export async function getNuwweFieldSettings(): Promise<T_fieldSettings> {
  const url = `${BASE_URL}/api/nuwwe/field-settings`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    // credentials: 'include'
  });

  const result = await response.json();

  return result;
}

export async function getNuwweCities(name: string = ""): Promise<T_options> {
  const url = `${BASE_URL}/api/nuwwe/cities?name=${name}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    // credentials: 'include'
  });

  const result = await response.json();

  return result;
}

type T_getNuwweNeighborhood_res = Promise<
  Array<{ id: string; city_id: string; name: string }>
>;

export async function getNuwweNeighborhood(
  name: string = ""
): T_getNuwweNeighborhood_res {
  const url = `${BASE_URL}/api/nuwwe/neighborhoods?name=${name}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    // credentials: 'include'
  });

  const result = await response.json();

  return result;
}

export type T_createNuwweClient_args = {
  cod_ciudad_cliente: string;
  cod_ciudad_documento: string;
  cod_cliente: string;
  cod_tipo_cliente: string;
  cod_tipo_documento: string;
  dir_cliente: string;
  email: string;
  primer_apellido: string;
  primer_nombre: string;
  segundo_apellido: string;
  segundo_nombre: string;
  cod_barrio_cliente: string;
  tel_cliente: string;
};

type T_nuwwe_res<T> = Promise<{
  code: number;
  datos: T;
  msg: string;
  success: boolean;
}>;

type T_createNuwweClient_res = T_nuwwe_res<{ cod_cliente: string }>;

export async function createNuwweClient(
  data: T_createNuwweClient_args
): T_createNuwweClient_res {
  const url = `${BASE_URL}/api/nuwwe/clients`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
    // credentials: 'include'
  });

  if (!response.ok) {
    const errorResult = await response.json();

    throw new Error(errorResult?.message, { cause: errorResult.errors });
  }

  return await response.json();
}

export type T_createProperty_arg = {
  cod_sucursal: string;
  cod_barrio: string;
  cod_ciudad: string;
  cod_tipo_inmueble: string;
  cod_estrato: string;
  vlr_canon: number;
  vlr_administracion: number;
  dir_inmueble: string;
  obs_inmueble: string;
};

type T_createNuwweProperty_res = T_nuwwe_res<{ cod_inmueble: string }>;

export async function createNuwweProperty(
  data: T_createProperty_arg
): T_createNuwweProperty_res {
  const url = `${BASE_URL}/api/nuwwe/properties`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
    // credentials: 'include',
  });

  const result = await response.json();

  if (response.status !== 200) {
    throw new Error("Algo salio mal");
  }

  return result;
}

export async function createNuwweDistributions(code: string, data) {
  const url = `${BASE_URL}/api/nuwwe/properties/${code}/distributions`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
    // credentials: 'include',
  });

  const result = await response.json();

  return result;
}

/**
 * Add Nuwwe images to property.
 *
 * @param {string} code Property code
 * @param {Array<string>} data An array of images
 *
 * @returns
 */
export async function createNuwweImages(code: string, data: Array<string>) {
  const url = `${BASE_URL}/api/nuwwe/properties/${code}/images`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
    // credentials: 'include',
  });

  const result = await response.json();

  return result;
}

export async function createNuwweDocuments(code: string, data) {
  const url = `${BASE_URL}/api/nuwwe/properties/${code}/documents`;
  const response = await fetch(url, {
    method: "POST",
    body: data,
    // credentials: 'include',
  });

  const result = await response.json();

  return result;
}

export async function createZohoLead(
  user: { cellphone: string },
  client: T_clientForm,
  property: T_propertyForm,
  branchOfficeName: string,
  code?: string
) {
  const propertyType =
    property.cod_tipo_inmueble === "APT" ? "Apartamento" : "Casa";

  // Validate client isn't empty.
  if (Object.keys(client).length > 0 && Object.keys(property).length > 0) {
    const body = {
      data: [
        {
          First_Name: client.primer_nombre,
          Last_Name: client.primer_apellido,
          Owner: "2528071000008997151",
          Email: client.email,
          Mobile: user.cellphone,
          Sucursal: branchOfficeName,
          Regional: "Medellín",
          Tipo_de_Documento: client.cod_tipo_documento.label,
          C_dula_propietario: client.cod_cliente,
          Documentoid: client.cod_cliente,
          Destinaci_n_del_inmueble: "Vivienda",
          Valor_de_propiedad_arriendo: "",
          Titular: "Apoderado",
          Tipo_de_Inmueble_de_Inter_s: propertyType,
          Servicio_de_inter_s_1: "Vivienda",
          Origen_de_Informaci_n: "Digital",
          Medio_Publicitario: "Landing autoconsignación",
          Servicio_interes: "Arriendo",
          Valor_canon_de_arrendamiento: property.vlr_canon,
          Estrategia_Campa_a: "Facebook",
          Tipo_de_Canal: "Digital",
          Tipo_de_Contacto: "Otros",
          Layout: { id: "2528071000000458001" },
          Observaciones_del_cliente: "",
        },
      ],
    };

    // Building observations
    let observations: Array<string> = [];

    if (parseInt(property.vlr_canon) < ZOHO_MIN_LEASING_VALUE) {
      observations.push(
        `El valor del arriendo ${Number(property.vlr_canon).toLocaleString(
          "es-CO"
        )} es menor a 1.200.000`
      );
    }

    if (code) {
      observations.push(`Código inmueble: ${code}`);
    }

    if (!Boolean(parseInt(property.independent_public_services))) {
      observations.push("No cuenta con servicios públicos independientes");
    } else {
      observations.push("Cuenta con servicios públicos independientes.");
    }

    if (observations.length > 0) {
      body.data[0].Observaciones_del_cliente = observations.join(" | ");
    }

    const url = "https://api.coninsa.co/api/v2/zohocrm-create-lead";
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
      // credentials: 'include',
    });

    const result = await response.json();
    return result;
  } else {
    return null;
  }
}

type T_branchOffice = { sucursal: { regional: string; sucursal: string } };

export const getNearBranchOffice = async (address: string): Promise<T_branchOffice> => {
  const url = "https://api.coninsa.co/api/v2/sucursal-cercana";
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ direccion: address }),
    redirect: "follow",
  });

  const result = await response.json();

  return result;
};

export async function logoutUser() {
  const url = `${BASE_URL}/user/logout`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    redirect: "follow",
    credentials: "include",
  });

  const result = await response.json();

  return result;
}
